import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Firestore, collection, query, where, orderBy, limit, collectionData } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FooterComponent } from '../../components/footer-main/footer-main';
import { FooterDesktopComponent } from '../../components/footer-desktop/footer-desktop';
import { VolCardComponent } from '../../components/vol-card/vol-card';
import { HeaderComponent } from '../../components/header-main/header-main';
import { HeaderDesktopComponent } from '../../components/header-desktop/header-desktop';
import { SearchClassifications } from '../../components/search-classifications/search-classifications';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// Import specific Ionic components
import { IonHeader, IonContent, IonToolbar, IonButtons, IonIcon, 
         IonTitle, IonGrid, IonRow, IonCol, IonSearchbar, 
         IonCard, IonCardHeader, IonCardTitle, IonCardContent, 
         IonCardSubtitle, IonFab, IonFabButton, IonButton, 
         IonInfiniteScroll, IonInfiniteScrollContent, IonFooter } from '@ionic/angular/standalone';

@Component({
    imports: [
        CommonModule, 
        DatePipe, 
        RouterModule, 
        FooterComponent, 
        FooterDesktopComponent, 
        VolCardComponent, 
        HeaderComponent, 
        HeaderDesktopComponent, 
        SearchClassifications, 
        // Ionic components
        IonHeader,
        IonContent,
        IonToolbar,
        IonButtons,
        IonIcon,
        IonTitle,
        IonGrid,
        IonRow,
        IonCol,
        IonSearchbar,
        IonCard,
        IonCardHeader,
        IonCardTitle,
        IonCardContent,
        IonCardSubtitle,
        IonFab,
        IonFabButton,
        IonButton,
        IonInfiniteScroll,
        IonInfiniteScrollContent,
        IonFooter
    ],
    selector: 'app-vols',
    standalone: true,
    host: { ngSkipHydration: 'true' },
    styleUrls: ['./volunteers.page.scss'],
    templateUrl: './volunteers.page.html',
})
export class VolunteersPage implements OnInit {
  innerWidth: number = 0;
  today: number = 0;
  @ViewChild("content", { static: true }) content: any;

  fbCollectionName: string = 'ge_vols';
  detailUrl       : string = '/volunteers/detail/';
  id: string = '';
  grouping: string | undefined;
  classification: string | undefined;
  slug: string = '';
  uid: string = '';
  detail: boolean = false;
  filterby: boolean = false;

  items$!: Observable<any[]>;
  locationFilter$ = new BehaviorSubject<string | null>(null);
  countryFilter$ = new BehaviorSubject<string | null>(null);
  regionFilter$ = new BehaviorSubject<string | null>(null);
  groupFilter$ = new BehaviorSubject<string | null>(null);
  classFilter$ = new BehaviorSubject<string | null>(null);

  searchGrouping: any;
  searchClass: any;
  searchLocation: any;
  searchCountry: any;
  searchRegion: any;
  searchType: any;
  searchSalary: any;
  searchRate: any;
  searchValue: any;
  searchMin: number = 3;

  dataList: any = [];
  filteredItems: any = [];

  errorMessage: boolean = false;
  loading: boolean = false;
  infiniteScroll: IonInfiniteScroll | undefined;

  filtersObj : any = {
    group: '',
    class: '',
    location: '',
    name: '',
    country: '',
    region: '',
    type: ''
  };

  title = 'Future of Work & Volunteering';
  description = 'Advertise Future of Work volunteering needs. Find volunteers looking to help charities and in the community on our volunteer board marketplace volunteer site platform globally across 39 countries in Australia, Austria, Belgium, Bulgaria, Canada, Cyprus, Czech Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hong Kong, Hungary, Ireland, Italy, Japan, Latvia, Lithuania, Luxembourg, Malaysia, Malta, Netherlands, New Zealand (NZ), Norway, Peru, Poland, Portugal, Romania, Singapore, Slovakia, Slovenia, Spain, Sweden, Switzerland, United Arab Emirates (UAE), United Kingdom (UK) & United States of America (USA).';

  constructor(
    public auth: Auth,
    private firestore: Firestore,
    private metaService: Meta,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
  ) {
  }

  ngOnInit() {
    this.titleService.setTitle(this.title);
    this.metaService.updateTag({ name: 'description', property: 'og:description', content: this.description })

    this.today = Date.now();
    this.searchQuery();
    if (typeof window !== 'undefined') {
        this.innerWidth = window.innerWidth;
        this.filterby = this.innerWidth >= 992;
    }
  }

  getData() {
    this.locationFilter$ = new BehaviorSubject<string | null>(null);
    this.countryFilter$ = new BehaviorSubject<string | null>(null);
    this.regionFilter$ = new BehaviorSubject<string | null>(null);
    this.groupFilter$ = new BehaviorSubject<string | null>(null);
    this.classFilter$ = new BehaviorSubject<string | null>(null);
    if (this.filtersObj.location) {
      this.locationFilter$.next(this.filtersObj.location);
    } else {
      this.locationFilter$.next(null);
    }
    if (this.filtersObj.country) {
      this.countryFilter$.next(this.filtersObj.country);
    } else {
      this.countryFilter$.next(null);
    }
    if (this.filtersObj.region) {
      this.regionFilter$.next(this.filtersObj.region);
    } else {
      this.regionFilter$.next(null);
    }
    if (this.filtersObj.group) {
      this.groupFilter$.next(this.filtersObj.group);
    } else {
      this.groupFilter$.next(null);
    }
    if (this.filtersObj.class) {
      this.classFilter$.next(this.filtersObj.class);
    } else {
      this.classFilter$.next(null);
    }
    this.items$ = combineLatest([
      this.locationFilter$,
      this.countryFilter$,
      this.regionFilter$,
      this.groupFilter$,
      this.classFilter$
    ]).pipe(
      switchMap(([location, country, region, grouping, classification]) => {
        const volsRef = collection(this.firestore, 'ge_vols');
        let queryConstraints: any[] = [];

        if (location === 'onsite') {
          queryConstraints.push(where('location', '==', location));
          if (country) queryConstraints.push(where('country', '==', country));
          if (region) queryConstraints.push(where('region', '==', region));
        } else if (location === 'remote') {
          queryConstraints.push(where('location', '==', location));
        }

        if (classification) {
          queryConstraints.push(where('classification', 'array-contains', classification));
        } else if (grouping) {
          queryConstraints.push(where('grouping', 'array-contains', grouping));
        }

        queryConstraints.push(where('visibility', '==', true));
        queryConstraints.push(orderBy('unpublish', 'desc'));

        const limitCount = (country && region) || (grouping && classification) ? 100 :
                         location || country || region || grouping || classification ? 250 : 100;
        queryConstraints.push(limit(limitCount));

        return collectionData(query(volsRef, ...queryConstraints));
      })
    );
  }

  searchQuery() {
    const firstParam: string | null = this.route.snapshot.queryParamMap.get('query');
    if (firstParam !== null) {
      this.initItems(firstParam);
      this.searchValue = firstParam;
    } else {
      this.initItems('');
    }
  }

  // Reinitialize items
  initItems(search: string) {
    this.loading = true;
    this.getData();
    let self = this;
    this.filteredItems = [];
    this.dataList = [];
    this.items$.pipe(
      map(list => {
        list.forEach(item => {
          let itemStatus = false;
          let filter = false;

          // Filter and search empty
          if ( !filter && (search == '') ) {
            itemStatus = true;
          }

          // Filter empty search not
          if ( !filter && (search != '') ) {
            itemStatus = self.searchFilter(search, item) ? true : false;
          }

          if (itemStatus) {
            self.filteredItems.push(item);
            const x = self.filteredItems
            .reduce(
              (accumulator: any[], current: { id: any; }) => accumulator.some(x => x.id === current.id)? accumulator: [...accumulator, current ], []
            )
            self.filteredItems = x;
          }

          return itemStatus;
        })

        for (let i = 0; i < 5; i++) {
          if (self.dataList.length < self.filteredItems.length) {
            self.dataList.push(self.filteredItems[self.dataList.length]);
          }
        }

        this.errorMsg();
        this.loading = false;
      })
    ).subscribe();

  }

  // Get ad detail
  adDetail(id: string, slug: string) {
    this.id = id;
    this.slug = slug;
    this.router.navigate([this.detailUrl + this.slug ]);
  }

  clearFilters(e: any) {
    this.filtersObj = e;
    this.searchValue = null;
    this.initItems('');
  }

  getFilters(e: any) {
    this.filtersObj = e;
    this.searchValue = null;
    this.initItems('');
  }

  getSearch(elem: any) {
    let key = elem.srcElement.value;
    if (key == null || key.length >= this.searchMin || key === '') {
      this.searchValue = key;
      this.initItems(key);
    }
  }

  searchFilter(key: string, item: { title: string; overview: string; }) {
    let str = String(item.title) + ' ' + String(item.overview);
    str = str.toLowerCase();
    return str.includes(key.toLowerCase()) ? true : false;
  }

  errorMsg() {
    if (this.filteredItems.length == 0 && !this.loading) {
      this.errorMessage = true;
    } else {
      this.errorMessage = false;
    }
  }

  loadData(event: any) {
    setTimeout(() => {
      if (this.dataList.length < this.filteredItems.length) {
        for (let i = 0; i < 5; i++) {
          if (this.dataList.length < this.filteredItems.length) {
            this.dataList.push(this.filteredItems[this.dataList.length]);
          }
        }
      }
      event.target.complete();
    }, 1000);
  }
}
